import React, { useState, useEffect, FC, SetStateAction, Dispatch } from 'react';
import * as ST from './styled';
import { ACTIONS } from '~/src/constants/analytics';
import { dataLayerForCBPhonePush } from '~/src/helpers/eventsForDataLayer';

export type TField = {
    value: string,
    isValid: boolean,
    clearInput: () => void,
}

type TMessage = {
    ok: boolean,
    text: string,
}

export interface ISubmitParam {
    fio: string,
    phone: string,
}

interface IProps {
    isLoading?: boolean,
    isVertical?: boolean,
    isPopup?: boolean,
    withAgreeLink?: boolean,
    option?: string,
    message?: TMessage,
    btnText?: string | React.ReactNode,
    submitRequest: (param: ISubmitParam) => void,
    formFilledHandler?: () => void,
}

/**
 *
 * @param {boolean} isLoading - отображение спинера загрузки
 * @param {boolean} isVertical - вертикальное расположение полей на всех размерах экрана
 * @param {object} message - сообщение, о результате, внизу формы
 * @param {function} submitRequest - колбэк при клике на кнопку
 * @param btnText
 * @param withAgreeLink
 * @param formFilledHandler
 * @param option - выбранная тема завявки для GTM
 * @param isPopup - место вызова события
 */
export const RequestForm:FC<IProps> = ({
    isLoading,
    isVertical,
    message,
    submitRequest,
    btnText = 'Отправить заявку',
    withAgreeLink = true,
    formFilledHandler,
    option,
    isPopup,
}: IProps) => {
    const [phone, setPhone] = useState<TField>();
    const [name, setName] = useState<TField>();
    const [isValidInputs, setIsValidInputs] = useState<boolean>(false);
    const [startInput, setStartInput] = useState<boolean>(false);

    useEffect(() => {
        setIsValidInputs(!!(phone?.isValid && name?.isValid));
        if (phone?.isValid && name?.isValid && formFilledHandler) {
            formFilledHandler();
        }
    }, [phone, name, formFilledHandler]);

    useEffect(() => {
        setStartInput(false);
    }, [option]);

    const onChangeHandler = (stateInput: TField, _setState: Dispatch<SetStateAction<TField | undefined>>) => {
        // @TODO stateInput.value убрать из условия после фикса nameDecorator (useEffect onchange) UI react
        if (!startInput && stateInput.value !== undefined) {
            dataLayerForCBPhonePush({ action: ACTIONS.started, requestId: null, option, isPopup });
            setStartInput(true);
        }
        _setState(stateInput);
    };

    const submitHandler = () => {
        if (isValidInputs) {
            submitRequest({
                fio: name?.value || '',
                phone: phone?.value || '',
            });
            phone?.clearInput();
            name?.clearInput();
            setPhone(undefined);
            setName(undefined);
        }
    };

    return (
        <ST.RequestFormWrapper>
            <ST.Inputs isVertical={isVertical}>
                <ST.Name
                    name='name'
                    onChange={(stateInput: TField) => onChangeHandler(stateInput, setName)}
                    placeholder='Ваше имя'
                    errorMessage='Ожидаются только буквы и дефисы'
                    isVertical={isVertical}
                    data-test='field-name'
                />
                <ST.Phone
                    name='phone'
                    onChange={(stateInput: TField) => onChangeHandler(stateInput, setPhone)}
                    placeholder='Номер телефона'
                    errorMessage='Телефон в формате +7 (999) 999-99-99'
                    data-test='field-phone'
                />
            </ST.Inputs>
            <ST.ButtonBlock isVertical={isVertical}>
                <ST.Button
                    onClick={submitHandler}
                    disabled={!isValidInputs || isLoading}
                    isVertical={isVertical}
                    data-test='submit-btn'
                    // @ts-ignore
                    id='submit'
                >{btnText}</ST.Button>
                {withAgreeLink && (
                    <ST.Agree>
                        Нажимая на кнопку, вы соглашаетесь на обработку
                        <ST.AgreeLink
                            href='/policy/app'
                            target='_blank'
                            rel='noopener noreferrer'
                            data-test='agreement'
                        >
                            персональных данных
                        </ST.AgreeLink>
                    </ST.Agree>
                )}
            </ST.ButtonBlock>
            {message
                ? <ST.Message ok={message?.ok}>{message?.text}</ST.Message>
                : null
            }
            <ST.StyledThrobber waitStatus={!isLoading}/>
        </ST.RequestFormWrapper>
    );
};
